// ServiceSettingsPage — parks.json halduse UI.
// Lubab UI kaudu muuta iga pargi nime, host'i, slave ID-d, PCS ja BAU seadistust.
// Salvestamisel kirjutab backend parks.json üle ja taaslaadib mällu (cache reset).

import { useState, useEffect } from 'react';
import { Header } from '../components/Layout';
import { useParks } from '../contexts/ParksContext';
import { useAuth } from '../contexts/AuthContext';
import { Save, RotateCcw, AlertCircle, CheckCircle2, Network, Cpu, AlertTriangle, Power, Square, Zap, Snowflake, RefreshCw } from 'lucide-react';

export function ServiceSettingsPage({ wsConnected }) {
  const [tab, setTab] = useState('network');  // 'network' | 'system'

  return (
    <div className="h-full flex flex-col" style={{ background: 'var(--bg)' }}>
      <Header title="Hooldus · Seadistused" connected={wsConnected} />

      <div style={{ flex: 1, overflow: 'auto', padding: '20px 28px 40px', maxWidth: 1480, width: '100%', margin: '0 auto' }}>
        {/* Alamkategooriate tab'id */}
        <div style={{
          display: 'flex', gap: 1, marginBottom: 18,
          background: 'var(--border)', border: '1px solid var(--border)',
          borderRadius: 6, overflow: 'hidden', width: 'fit-content',
        }}>
          <TabBtn active={tab === 'network'} onClick={() => setTab('network')} icon={<Network size={13} />}>
            Võrguseadistused
          </TabBtn>
          <TabBtn active={tab === 'system'} onClick={() => setTab('system')} icon={<Cpu size={13} />}>
            Süsteemi seadistused
          </TabBtn>
        </div>

        {tab === 'network' && <NetworkSettingsPanel />}
        {tab === 'system'  && <SystemSettingsPanel />}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '7px 14px',
        background: active ? '#18181b' : 'var(--surface)',
        color: active ? 'white' : 'var(--text-2)',
        border: 'none', cursor: 'pointer',
        fontFamily: 'inherit', fontSize: '0.72rem', fontWeight: 500,
      }}
    >
      {icon} {children}
    </button>
  );
}

// =====================================================================
// Võrguseadistused — parks.json haldus
// =====================================================================
function NetworkSettingsPanel() {
  const [config, setConfig] = useState(null);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [inverterTypes, setInverterTypes] = useState([]);
  const [reloading, setReloading] = useState(false);
  const [reloadedAt, setReloadedAt] = useState(null);
  const [tenants, setTenants] = useState([]);
  const { isSuperadmin, canAddDevices, viewTenant } = useAuth();

  const loadConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const [resCfg, resTypes, resTenants] = await Promise.all([
        fetch('/api/parks/config'),
        fetch('/api/parks/inverter-types'),
        fetch('/api/auth/tenants'),
      ]);
      if (!resCfg.ok) throw new Error(`HTTP ${resCfg.status}`);
      const json = await resCfg.json();
      // Deye: vana `deye` blokk (üks seade) → deye_inverters nimekiri (UI haldab nimekirja)
      json.parks = (json.parks || []).map((p) => (
        p.meter_protocol === 'deye_v5' && !(p.deye_inverters || []).length && p.deye?.host
          ? { ...p, deye_inverters: [{ ...p.deye }] } : p));
      if (resTenants.ok) setTenants((await resTenants.json()).tenants || []);
      setConfig(json);
      setOriginal(JSON.parse(JSON.stringify(json)));
      if (resTypes.ok) {
        const tj = await resTypes.json();
        setInverterTypes(tj.types || []);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  // Uus park — slot = järgmine vaba üle KÕIGI klientide (backend next_free_slot)
  const addPark = () => {
    setConfig((prev) => {
      const slot = Math.max(prev.next_free_slot || 1, ...prev.parks.map((p) => (p.slot || 0) + 1));
      return {
        ...prev,
        next_free_slot: slot + 1,
        parks: [...prev.parks, { slot, name: '', host: '', meter_protocol: null,
                                 tenant_id: viewTenant || 'vimpla' }],
      };
    });
  };

  // Deye hübriid seadmed (deye_inverters) — logger SN tuvastatakse automaatselt
  const setDeyeList = (slot, fn) => {
    setConfig((prev) => ({
      ...prev,
      parks: prev.parks.map((p) => p.slot !== slot ? p : ({ ...p, deye_inverters: fn(p.deye_inverters || []) })),
    }));
  };
  const addDeye = (slot) => setDeyeList(slot, (l) => [...l, { host: '', port: 8899, label: '' }]);
  const updateDeye = (slot, idx, field, value) =>
    setDeyeList(slot, (l) => l.map((d, i) => i !== idx ? d : { ...d, [field]: value }));
  const removeDeye = (slot, idx) => setDeyeList(slot, (l) => l.filter((_, i) => i !== idx));

  // PV stringid (MPPT) — Deye monitooringu analüüsiks (oodatud pinge/toodang paneelide järgi)
  const setPvStrings = (slot, fn) => {
    setConfig((prev) => ({
      ...prev,
      parks: prev.parks.map((p) => p.slot !== slot ? p : ({ ...p, pv_strings: fn(p.pv_strings || []) })),
    }));
  };
  const addPvString = (slot) => setPvStrings(slot, (l) => {
    const last = l[l.length - 1] || {};
    // Uus string kopeerib eelmise paneeli andmed (tavaliselt sama paneel)
    const panel = Object.fromEntries(PV_PANEL_FIELDS.map((f) => [f.key, last[f.key] ?? '']));
    return [...l, { mppt: Math.max(0, ...l.map((x) => Number(x.mppt) || 0)) + 1, label: '', panels_series: '', strings_parallel: 1,
                    tilt: last.tilt ?? 35, azimuth: last.azimuth ?? 0, ...panel }];
  });
  const updatePvString = (slot, idx, field, value) =>
    setPvStrings(slot, (l) => l.map((x, i) => i !== idx ? x : { ...x, [field]: value }));
  const removePvString = (slot, idx) => setPvStrings(slot, (l) => l.filter((_, i) => i !== idx));
  const updateLocation = (slot, field, value) => {
    setConfig((prev) => ({
      ...prev,
      parks: prev.parks.map((p) => p.slot !== slot ? p : ({ ...p, forecast: { ...(p.forecast || {}), [field]: value === '' ? null : Number(value) } })),
    }));
  };

  // Inverter helpers
  const addInverter = (slot) => {
    setConfig((prev) => ({
      ...prev,
      parks: prev.parks.map((p) => p.slot !== slot ? p : ({
        ...p,
        inverters: [...(p.inverters || []), {
          type: inverterTypes[0]?.id || 'GW50K-MT',
          host: '10.0.0.120',
          port: inverterTypes[0]?.default_port || 502,
          slave_id: inverterTypes[0]?.default_slave_id || 1,
          label: '',
        }],
      })),
    }));
  };
  const updateInverter = (slot, idx, field, value) => {
    setConfig((prev) => ({
      ...prev,
      parks: prev.parks.map((p) => p.slot !== slot ? p : ({
        ...p,
        inverters: p.inverters.map((inv, i) => i !== idx ? inv : { ...inv, [field]: value }),
      })),
    }));
  };
  const removeInverter = (slot, idx) => {
    setConfig((prev) => ({
      ...prev,
      parks: prev.parks.map((p) => p.slot !== slot ? p : ({
        ...p,
        inverters: p.inverters.filter((_, i) => i !== idx),
      })),
    }));
  };

  const updatePark = (slot, field, value) => {
    setConfig((prev) => ({
      ...prev,
      parks: prev.parks.map((p) =>
        p.slot === slot ? { ...p, [field]: value === '' ? null : value } : p
      ),
    }));
  };

  // Sisuline välja muutus pesastatud blokis (_meta v või control)
  const updateNested = (slot, parent, field, value) => {
    setConfig((prev) => ({
      ...prev,
      parks: prev.parks.map((p) => {
        if (p.slot !== slot) return p;
        const nested = { ...(p[parent] || {}) };
        if (value === '' || value === null || value === undefined) {
          delete nested[field];
        } else {
          nested[field] = value;
        }
        return { ...p, [parent]: Object.keys(nested).length ? nested : null };
      }),
    }));
  };

  const updateDefaults = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      defaults: { ...prev.defaults, [field]: value === '' ? null : value },
    }));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // Eemalda undefined/null väärtused kuhu mitte vaja
      const cleaned = {
        ...config,
        parks: config.parks.map((p) => {
          const out = { slot: p.slot, name: p.name || null, host: p.host };
          // Optsionaalsed väljad — ainult kui olemas
          ['pcs_host', 'pcs_port', 'pcs_slave', 'bau_host', 'bau_port', 'bau_slave',
           'port', 'slave_solar', 'slave_grid', 'grid_host', 'grid_port',
           'meter_protocol', 'meter_host', 'meter_port', 'meter_interval', 'meter_timeout'].forEach((k) => {
            if (p[k] !== null && p[k] !== undefined && p[k] !== '') out[k] = p[k];
          });
          // Säilita kommentaariväljad (kõik mis algavad _-ga, v.a sisemised UI flag'id)
          for (const key of Object.keys(p)) {
            if (key.startsWith('_') && key !== '_dirty' && !(key in out)) {
              out[key] = p[key];
            }
          }
          // Olek/halduse lipud — ALATI saada eksplitsiitne väärtus, et backend merge
          // saaks UI-st kustutused üle kirjutada (vana bug: kui muted=true salvestati ja
          // hiljem UI-st maha võetakse, vana out.muted polnud false → merge säilitas true).
          out.disabled = p.disabled === true;
          out.muted = p.muted === true;
          out.display_message = (typeof p.display_message === 'string' && p.display_message) ? p.display_message : null;
          out.muted_reason = (typeof p.muted_reason === 'string' && p.muted_reason) ? p.muted_reason : null;
          // Kontrolli konf (soc_min/max), sxy88 mõõdikud, UI metaandmed — säilita üks-ühele
          if (p.control && typeof p.control === 'object') out.control = p.control;
          if (p.sxy88_meters) out.sxy88_meters = p.sxy88_meters;
          if (p._meta) out._meta = p._meta;
          // Solar forecast konf (forecast.solar API)
          if (p.forecast && typeof p.forecast === 'object') out.forecast = p.forecast;
          // Klient (tenant) — ainult superadmin saab valida (backend jõustab nagunii)
          if (isSuperadmin && p.tenant_id) out.tenant_id = p.tenant_id;
          // Deye hübriid: seadmete nimekiri; vana `deye` blokk null (migreeritud nimekirja)
          if (p.meter_protocol === 'deye_v5') {
            out.deye_inverters = (p.deye_inverters || [])
              .filter((d) => d.host)
              .map((d) => ({
                host: d.host.trim(),
                port: parseInt(d.port) || 8899,
                ...(d.label ? { label: d.label } : {}),
                ...(d.logger_serial ? { logger_serial: Number(d.logger_serial) } : {}),
              }));
            out.deye = null;
            if (!out.host) out.host = out.deye_inverters[0]?.host || '';
            out.pv_strings = (p.pv_strings || [])
              .filter((x) => Number(x.mppt) > 0 && Number(x.panels_series) > 0)
              .map((x) => {
                const o = { mppt: Number(x.mppt) };
                if (x.label) o.label = String(x.label);
                if (x.device) o.device = x.device;
                for (const k of PV_NUMERIC_KEYS) {
                  const v = parseFloat(String(x[k] ?? '').replace(',', '.'));
                  if (Number.isFinite(v)) o[k] = v;
                }
                return o;
              });
          }
          // Inverters — säilita kui mitte-tühi
          if (Array.isArray(p.inverters) && p.inverters.length > 0) {
            out.inverters = p.inverters.map((inv) => ({
              type: inv.type,
              host: inv.host,
              port: parseInt(inv.port) || 502,
              slave_id: parseInt(inv.slave_id) || 1,
              ...(inv.label ? { label: inv.label } : {}),
              ...(inv.use_telegraf ? { use_telegraf: true } : {}),
            }));
          }
          return out;
        }),
      };
      const res = await fetch('/api/parks/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleaned),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || `HTTP ${res.status}`);
      setSavedAt(Date.now());
      await loadConfig();  // reload, et UI näitaks salvestatud + valideeritud sisu
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const reloadSites = async () => {
    if (!confirm('Rakenda Modbus-tasandi muudatused?\n\nKõik aktiivsed SiteController-id (PCS / BAU / meter ühendused) sulgetakse ja taasluuakse parks.json-i järgi. PlanController-i juhitavate parkide juures võib esineda 1–3 s katkestus.')) return;
    setReloading(true);
    setError(null);
    try {
      const r = await fetch('/api/sites/reload', { method: 'POST' });
      const j = await r.json();
      if (!r.ok) throw new Error(j.message || `HTTP ${r.status}`);
      setReloadedAt(Date.now());
    } catch (e) {
      setError(`Sites reload viga: ${e.message}`);
    } finally {
      setReloading(false);
    }
  };

  const isDirty = config && original && JSON.stringify(config) !== JSON.stringify(original);

  return (
    <div>
        {/* Action bar */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 16, padding: '10px 14px',
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
        }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-3)' }}>
            {loading && 'Laadin...'}
            {!loading && config && (
              <span>
                {config.parks?.length || 0} parki konfiguratsioonis
                {isDirty && <span style={{ color: 'var(--warning)', marginLeft: 8 }}>· salvestamata muudatused</span>}
              </span>
            )}
            {error && (
              <span style={{ color: 'var(--negative)' }}>
                <AlertCircle size={12} style={{ display: 'inline', verticalAlign: -2 }} /> {error}
              </span>
            )}
            {savedAt && Date.now() - savedAt < 5000 && (
              <span style={{ color: 'var(--positive)', marginLeft: 8 }}>
                <CheckCircle2 size={12} style={{ display: 'inline', verticalAlign: -2 }} /> Salvestatud
              </span>
            )}
            {reloadedAt && Date.now() - reloadedAt < 5000 && (
              <span style={{ color: 'var(--positive)', marginLeft: 8 }}>
                <CheckCircle2 size={12} style={{ display: 'inline', verticalAlign: -2 }} /> Modbus-tasand uuendatud
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={reloadSites}
              disabled={reloading || saving}
              title="Sulgeb ja taasloob kõik SiteController Modbus-ühendused (read_only, pcs_polling_disabled, host'i muudatused). Vajalik kui muutsid Modbus-tasandi lippe ja tahad et muudatus jõustuks ilma backend restart-ita."
              style={{
                padding: '5px 11px', borderRadius: 4,
                border: '1px solid var(--border-strong)', background: 'white',
                color: 'var(--text-2)', cursor: reloading || saving ? 'not-allowed' : 'pointer',
                opacity: reloading || saving ? 0.4 : 1,
                fontSize: '0.7rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              <RefreshCw size={12} style={{ animation: reloading ? 'spin 1s linear infinite' : 'none' }} />
              {reloading ? 'Rakendan...' : 'Rakenda Modbus muudatused'}
            </button>
            <button
              onClick={() => setConfig(JSON.parse(JSON.stringify(original)))}
              disabled={!isDirty || saving}
              style={{
                padding: '5px 11px', borderRadius: 4,
                border: '1px solid var(--border-strong)', background: 'white',
                color: 'var(--text-2)', cursor: isDirty && !saving ? 'pointer' : 'not-allowed',
                opacity: isDirty && !saving ? 1 : 0.4,
                fontSize: '0.7rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              <RotateCcw size={12} /> Tühista
            </button>
            <button
              onClick={save}
              disabled={!isDirty || saving}
              style={{
                padding: '5px 11px', borderRadius: 4,
                border: '1px solid #18181b', background: '#18181b', color: 'white',
                cursor: isDirty && !saving ? 'pointer' : 'not-allowed',
                opacity: isDirty && !saving ? 1 : 0.4,
                fontSize: '0.7rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              <Save size={12} /> {saving ? 'Salvestan...' : 'Salvesta'}
            </button>
          </div>
        </div>

        {/* Defaults sektsioon — globaalne (kõik kliendid) → ainult superadmini täisvaates */}
        {config?.defaults && isSuperadmin && !viewTenant && (
          <Section title="Vaikeväärtused (rakendub kõikidele parkidele kus pole spetsiifikat)">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
              <Field label="Mõõdiku port" value={config.defaults.port}        onChange={(v) => updateDefaults('port', parseInt(v) || null)} type="number" />
              <Field label="PV slave"      value={config.defaults.slave_solar} onChange={(v) => updateDefaults('slave_solar', parseInt(v) || null)} type="number" />
              <Field label="GRID slave"    value={config.defaults.slave_grid}  onChange={(v) => updateDefaults('slave_grid', parseInt(v) || null)} type="number" />
              <Field label="PCS port"      value={config.defaults.pcs_port}    onChange={(v) => updateDefaults('pcs_port', parseInt(v) || null)} type="number" />
              <Field label="PCS slave"     value={config.defaults.pcs_slave}   onChange={(v) => updateDefaults('pcs_slave', parseInt(v) || null)} type="number" />
            </div>
          </Section>
        )}

        {/* Pargid — iga park üks plokk koos seadmete ja inverteritega */}
        {config?.parks && (
          <Section title="Pargid (kõik seadmed ühes vaates)">
            <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', marginBottom: 10, fontStyle: 'italic' }}>
              💡 Iga park sisaldab mõõdikuid (PV+GRID), valikulist PCS, BAU ja invertereid. Salvestamisel kirjutatakse <code style={{ fontFamily: 'JetBrains Mono, monospace' }}>config/parks.json</code> üle ja Telegraf-config genereeritakse automaatselt.
            </div>
            {config.parks.map((p) => (
              <div key={p.slot} style={{
                marginBottom: 10, padding: '12px 14px',
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderLeft: p.name ? '3px solid #18181b' : '3px solid var(--border-strong)',
                borderRadius: 4,
              }}>
                {/* Pargi päis: slot + nimi */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: '0.85rem',
                                  background: 'var(--surface-2)', padding: '3px 8px', borderRadius: 3, minWidth: 36, textAlign: 'center' }}>
                    {p.slot}
                  </span>
                  <CellInput value={p.name ?? ''}
                    onChange={(v) => updatePark(p.slot, 'name', v || null)}
                    placeholder="Pargi nimi (tühi = mitte paigaldatud)"
                    style={{ flex: 1, fontSize: '0.85rem', fontWeight: 600 }} />
                  <select
                    value={p.meter_protocol || ''}
                    disabled={!isSuperadmin}
                    title={isSuperadmin ? 'Pargi tüüp — määrab, milliseid seadmeid pargil seadistatakse' : 'Pargi tüüpi muudab superadmin'}
                    onChange={(e) => updatePark(p.slot, 'meter_protocol', e.target.value || null)}
                    style={selectStyle}
                  >
                    {PARK_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                  {isSuperadmin && (
                    <select
                      value={p.tenant_id || 'vimpla'}
                      title="Klient, kellele park kuulub"
                      onChange={(e) => updatePark(p.slot, 'tenant_id', e.target.value)}
                      style={selectStyle}
                    >
                      {tenants.map((t) => <option key={t.id} value={t.id}>{t.name || t.id}</option>)}
                    </select>
                  )}
                </div>

                {p.meter_protocol === 'deye_v5' && (
                  <FieldGroup label="Deye hübriidinverter(id) — Solarman V5 logger (logger SN tuvastatakse automaatselt)">
                    <div style={{ gridColumn: '1 / -1' }}>
                      {(p.deye_inverters || []).length === 0 && (
                        <div style={{ fontSize: '0.66rem', color: 'var(--text-4)', fontStyle: 'italic', padding: '4px 0' }}>
                          Ühtegi Deye inverterit pole lisatud
                        </div>
                      )}
                      {(p.deye_inverters || []).map((d, idx) => (
                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1.4fr 90px 1fr 1fr 32px', gap: 6, marginBottom: 4, alignItems: 'center' }}>
                          <CellInput value={d.host || ''} mono placeholder="IP / hostinimi"
                            onChange={(v) => updateDeye(p.slot, idx, 'host', v)} />
                          <CellInput value={d.port ?? ''} type="number" mono placeholder="8899"
                            onChange={(v) => updateDeye(p.slot, idx, 'port', parseInt(v) || null)} />
                          <CellInput value={d.label || ''} placeholder="Silt (nt Maja)"
                            onChange={(v) => updateDeye(p.slot, idx, 'label', v)} />
                          <span style={{ fontSize: '0.66rem', color: 'var(--text-3)', fontFamily: 'JetBrains Mono, monospace' }}>
                            SN {d.logger_serial || 'automaatne'}
                          </span>
                          {canAddDevices ? (
                            <button onClick={() => removeDeye(p.slot, idx)} title="Eemalda Deye inverter" style={removeBtnStyle}>×</button>
                          ) : <span />}
                        </div>
                      ))}
                      {canAddDevices && (
                        <button onClick={() => addDeye(p.slot)} style={addBtnStyle}>+ Lisa Deye inverter</button>
                      )}
                    </div>
                  </FieldGroup>
                )}

                {p.meter_protocol === 'deye_v5' && (
                  <FieldGroup label="PV stringid (MPPT) — monitooringu analüüs: oodatud pinge ja toodang paneelide järgi">
                    <div style={{ gridColumn: '1 / -1', overflowX: 'auto' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: PV_GRID, gap: 4, minWidth: 1180, marginBottom: 2 }}>
                        {PV_COLUMNS.map((c) => (
                          <span key={c.key} title={c.hint || ''} style={{ fontSize: '0.56rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{c.label}</span>
                        ))}
                        <span />
                      </div>
                      {(p.pv_strings || []).length === 0 && (
                        <div style={{ fontSize: '0.66rem', color: 'var(--text-4)', fontStyle: 'italic', padding: '4px 0' }}>
                          Stringe pole sisestatud — monitooringu aruanne näitab ainult mõõtmisi (ilma oodatud pinge/toodanguta)
                        </div>
                      )}
                      {(p.pv_strings || []).map((x, idx) => (
                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: PV_GRID, gap: 4, minWidth: 1180, marginBottom: 4, alignItems: 'center' }}>
                          {PV_COLUMNS.map((c) => (
                            <CellInput key={c.key} value={x[c.key] ?? ''} mono={c.key !== 'label'} placeholder={c.placeholder}
                              style={{ minWidth: 0 }}
                              onChange={(v) => updatePvString(p.slot, idx, c.key, v)} />
                          ))}
                          <button onClick={() => removePvString(p.slot, idx)} title="Eemalda string" style={removeBtnStyle}>×</button>
                        </div>
                      ))}
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
                        <button onClick={() => addPvString(p.slot)} style={addBtnStyle}>+ Lisa string</button>
                        <span style={{ fontSize: '0.62rem', color: 'var(--text-3)' }}>Asukoht (ilmaandmed):</span>
                        <CellInput value={p.forecast?.lat ?? ''} mono placeholder="laius 59.40" style={{ width: 100, minWidth: 0 }}
                          onChange={(v) => updateLocation(p.slot, 'lat', v)} />
                        <CellInput value={p.forecast?.lon ?? ''} mono placeholder="pikkus 24.75" style={{ width: 100, minWidth: 0 }}
                          onChange={(v) => updateLocation(p.slot, 'lon', v)} />
                        <span style={{ fontSize: '0.6rem', color: 'var(--text-4)' }}>Asimuut: 0 = lõuna, −90 = ida, 90 = lääs. Paneeli andmed andmelehelt (STC).</span>
                      </div>
                    </div>
                  </FieldGroup>
                )}

                {p.meter_protocol !== 'deye_v5' && (<>

                {/* Mõõdikud (ex9EMS) — alati nähtaval. PV ja GRID jagavad host'i vaikimisi,
                    aga GRID võib olla eraldi host/port'il (grid_host/grid_port — jätta tühjaks kui sama kui PV). */}
                <FieldGroup label="PV mõõdik (ex9EMS)">
                  <Field label="Host" value={p.host ?? ''}
                    onChange={(v) => updatePark(p.slot, 'host', v)} placeholder="10.0.0.X0" mono />
                  <Field label="Port" value={p.port ?? ''} type="number"
                    onChange={(v) => updatePark(p.slot, 'port', parseInt(v) || null)}
                    placeholder={String(config.defaults?.port ?? 502)} mono />
                  <Field label="PV slave" value={p.slave_solar ?? ''} type="number"
                    onChange={(v) => updatePark(p.slot, 'slave_solar', parseInt(v) || null)}
                    placeholder={String(config.defaults?.slave_solar ?? 1)} mono />
                </FieldGroup>
                <FieldGroup label="GRID mõõdik (ex9EMS) — kui eraldi seade, sisesta grid_host/grid_port">
                  <Field label="GRID host" value={p.grid_host ?? ''}
                    onChange={(v) => updatePark(p.slot, 'grid_host', v || null)}
                    placeholder={p.host || '— sama kui PV host'} mono />
                  <Field label="GRID port" value={p.grid_port ?? ''} type="number"
                    onChange={(v) => updatePark(p.slot, 'grid_port', parseInt(v) || null)}
                    placeholder={`— sama kui PV port (${p.port || config.defaults?.port || 502})`} mono />
                  <Field label="GRID slave" value={p.slave_grid ?? ''} type="number"
                    onChange={(v) => updatePark(p.slot, 'slave_grid', parseInt(v) || null)}
                    placeholder={String(config.defaults?.slave_grid ?? 2)} mono />
                </FieldGroup>

                {/* PCS — valikuline */}
                <FieldGroup label="PCS (valikuline)">
                  <Field label="Host" value={p.pcs_host ?? ''}
                    onChange={(v) => updatePark(p.slot, 'pcs_host', v || null)} placeholder="—" mono />
                  <Field label="Slave" value={p.pcs_slave ?? ''} type="number"
                    onChange={(v) => updatePark(p.slot, 'pcs_slave', parseInt(v) || null)} placeholder="1" mono />
                </FieldGroup>

                {/* BAU — valikuline */}
                <FieldGroup label="BAU (valikuline)">
                  <Field label="Host" value={p.bau_host ?? ''}
                    onChange={(v) => updatePark(p.slot, 'bau_host', v || null)} placeholder="—" mono />
                  <Field label="Port" value={p.bau_port ?? ''} type="number"
                    onChange={(v) => updatePark(p.slot, 'bau_port', parseInt(v) || null)} placeholder="12230" mono />
                  <Field label="Slave" value={p.bau_slave ?? ''} type="number"
                    onChange={(v) => updatePark(p.slot, 'bau_slave', parseInt(v) || null)} placeholder="1" mono />
                </FieldGroup>

                {/* Peakaitse ja PCS võimsus — kasutatakse charge/discharge piirides ja PANIC override-is */}
                <FieldGroup label="Peakaitse ja võimsus (juhtimisalgoritmile)">
                  <Field label="Peakaitse sisse (A)" value={p._meta?.grid_in_a ?? ''} type="number"
                    onChange={(v) => updateNested(p.slot, '_meta', 'grid_in_a', v === '' ? null : parseInt(v) || null)}
                    placeholder="80" mono />
                  <Field label="Peakaitse välja (A)" value={p._meta?.grid_out_a ?? ''} type="number"
                    onChange={(v) => updateNested(p.slot, '_meta', 'grid_out_a', v === '' ? null : parseInt(v) || null)}
                    placeholder="80" mono />
                  <Field label="PCS võimsus (kW)" value={p._meta?.battery_power_kw ?? ''} type="number"
                    onChange={(v) => updateNested(p.slot, '_meta', 'battery_power_kw', v === '' ? null : parseInt(v) || null)}
                    placeholder="125" mono />
                </FieldGroup>

                {/* Juhtimise piirangud — kasutavad PlanController feedback-loop ja SoC taper */}
                <FieldGroup label="Juhtimise piirangud (battery_use / charging / max_sell)">
                  <Field label="SoC min (%)" value={p.control?.soc_min ?? ''} type="number"
                    onChange={(v) => updateNested(p.slot, 'control', 'soc_min', v === '' ? null : parseInt(v) || null)}
                    placeholder="10" mono />
                  <Field label="SoC max (%)" value={p.control?.soc_max ?? ''} type="number"
                    onChange={(v) => updateNested(p.slot, 'control', 'soc_max', v === '' ? null : parseInt(v) || null)}
                    placeholder="90" mono />
                  <Field label="Import limit (kW)" value={p.control?.import_target_kw ?? ''} type="number"
                    onChange={(v) => updateNested(p.slot, 'control', 'import_target_kw', v === '' ? null : parseFloat(v) || null)}
                    placeholder={`auto (${((p._meta?.grid_in_a ?? 80) * 0.625 * 0.85).toFixed(0)})`} mono />
                  <Field label="Export limit (kW)" value={p.control?.export_target_kw ?? ''} type="number"
                    onChange={(v) => updateNested(p.slot, 'control', 'export_target_kw', v === '' ? null : parseFloat(v) || null)}
                    placeholder={`auto (${((p._meta?.grid_out_a ?? 80) * 0.625 * 0.85).toFixed(0)})`} mono />
                </FieldGroup>

                {/* Automaatne plaan — AutoPlanner 15-min reeval */}
                <FieldGroup label="Automaatne plaan (kasumlikkuse optimeerija, 15-min reeval)">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <input
                      type="checkbox"
                      checked={p.control?.auto_planner_enabled === true}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        updateNested(p.slot, 'control', 'auto_planner_enabled', checked ? true : null);
                        // Aktiveerimisel kustuta trip-staatus (kasutaja kinnitab et debug tehtud)
                        if (checked && p.control?._tripped_at) {
                          updateNested(p.slot, 'control', '_tripped_at', null);
                          updateNested(p.slot, 'control', '_tripped_reason', null);
                        }
                      }}
                      id={`auto-planner-${p.slot}`}
                    />
                    <label htmlFor={`auto-planner-${p.slot}`} style={{ fontSize: '0.72rem', cursor: 'pointer' }}>
                      Aktiveeri automaatne planeerimine
                    </label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={p.control?.use_emhass === true}
                      onChange={(e) => updateNested(p.slot, 'control', 'use_emhass', e.target.checked ? true : null)}
                      id={`use-emhass-${p.slot}`}
                      disabled={!(p.control?.auto_planner_enabled === true)}
                    />
                    <label htmlFor={`use-emhass-${p.slot}`} style={{
                      fontSize: '0.72rem',
                      cursor: p.control?.auto_planner_enabled ? 'pointer' : 'not-allowed',
                      opacity: p.control?.auto_planner_enabled ? 1 : 0.5,
                    }}>
                      Kasuta EMHASS MILP optimeerijat (muidu L1 heuristika)
                    </label>
                  </div>
                  {/* TRIP-STATUS — kuvatakse kui park on automaatselt välja lülitatud overcurrent-i tõttu */}
                  {p.control?._tripped_at && (
                    <div style={{
                      marginTop: 6,
                      padding: '6px 8px',
                      background: 'rgba(220, 38, 38, 0.1)',
                      border: '1px solid rgba(220, 38, 38, 0.4)',
                      borderRadius: 4,
                      fontSize: '0.7rem',
                      color: '#dc2626',
                    }}>
                      <div style={{ fontWeight: 600, marginBottom: 2 }}>⛔ Auto-juhtimine TRIPP'I tõttu välja lülitatud</div>
                      <div>Aeg: {new Date(p.control._tripped_at).toLocaleString('et')}</div>
                      <div>Põhjus: {p.control._tripped_reason || '(teadmata)'}</div>
                      <div style={{ marginTop: 4, fontStyle: 'italic' }}>
                        Märgi "Aktiveeri automaatne planeerimine" linnuke uuesti, et taastada (kontrolli enne miks ületus juhtus).
                      </div>
                    </div>
                  )}
                </FieldGroup>

                </>)}

                {/* Muted — vaigistab alarmid globaalsest nimekirjast, polling jätkub */}
                <FieldGroup label="Vaigistus (alarmid peidetud globaalsest nimekirjast)">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={p.muted === true}
                      onChange={(e) => updatePark(p.slot, 'muted', e.target.checked || null)}
                      id={`muted-${p.slot}`}
                    />
                    <label htmlFor={`muted-${p.slot}`} style={{ fontSize: '0.72rem', cursor: 'pointer' }}>
                      Vaigista alarmid
                    </label>
                  </div>
                  <Field label="Põhjus" value={p.muted_reason ?? ''}
                    onChange={(v) => updatePark(p.slot, 'muted_reason', v || null)}
                    placeholder="nt: Markedroidi integreerimisel" />
                </FieldGroup>

                {p.meter_protocol !== 'deye_v5' && (<>
                {/* Markedroid-üleandmine — granulaarne PCS + inverter pollimise välja lülitus.
                    PCS ja/või inverter Modbus pollimine jätab EMS-i poolt välja, et väline
                    klient (nt Markedroid) saaks neid hallata ilma single-client-konflikti.
                    Meeter (PV + grid) pollimine jätkub — UI näeb pargi state-i edasi. */}
                <FieldGroup label="Markedroidi üleandmine (EMS ei pollu Modbus-i)">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <input
                      type="checkbox"
                      checked={p.control?.pcs_polling_disabled === true}
                      onChange={(e) => updateNested(p.slot, 'control', 'pcs_polling_disabled', e.target.checked || null)}
                      id={`pcs-pd-${p.slot}`}
                    />
                    <label htmlFor={`pcs-pd-${p.slot}`} style={{ fontSize: '0.72rem', cursor: 'pointer' }}>
                      🛑 ex9EMS Modbus pollimine välja (EMS ei loe PCS-i, grid_meter ega pv_meter)
                    </label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={p.control?.inverter_polling_disabled === true}
                      onChange={(e) => updateNested(p.slot, 'control', 'inverter_polling_disabled', e.target.checked || null)}
                      id={`inv-pd-${p.slot}`}
                    />
                    <label htmlFor={`inv-pd-${p.slot}`} style={{ fontSize: '0.72rem', cursor: 'pointer' }}>
                      🛑 Inverter Modbus pollimine välja (EMS ei loe ega curtail-i)
                    </label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                    <input
                      type="checkbox"
                      checked={p.control?.read_only === true}
                      onChange={(e) => updateNested(p.slot, 'control', 'read_only', e.target.checked || null)}
                      id={`ro-${p.slot}`}
                    />
                    <label htmlFor={`ro-${p.slot}`} style={{ fontSize: '0.72rem', cursor: 'pointer' }}>
                      🔒 READ_ONLY — kõik PCS / BAU / inverter Modbus write käsklused blokeeritud (defence-in-depth)
                    </label>
                  </div>
                  <div style={{ fontSize: '0.62rem', color: 'var(--text-4)', marginTop: 4, fontStyle: 'italic' }}>
                    Eeldus: PCS + grid_meter + pv_meter on samal ex9EMS gateway'l (10.0.0.X:502) ja Markedroid haldab seda. EMS jätab gateway täielikult vabaks. BAU pollimine jätkub eraldi otsest. PV-võimsus + täna kWh tulevad Markedroidi MQTT-st (vajab markedroid_mqtt_host).
                  </div>
                  <Field
                    label="Markedroid MQTT host"
                    value={p.control?.markedroid_mqtt_host ?? ''}
                    onChange={(v) => updateNested(p.slot, 'control', 'markedroid_mqtt_host', v || null)}
                    placeholder="nt 10.0.0.205:1883 (vaikimisi port 1883)"
                    mono
                  />
                  <div style={{ fontSize: '0.62rem', color: 'var(--text-4)', marginTop: 2, fontStyle: 'italic' }}>
                    Kui seatud, EMS kuulab Markedroidi MQTT-i (public/realtimedata/#) ja kasutab grid_meter + pv_meter andmeid Modbus-pollimise asemel. Aku SoC tuleb endiselt BAU otsest.
                  </div>
                </FieldGroup>

                {/* Inverterid — valikuline list */}
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                fontSize: '0.6rem', color: 'var(--text-3)', textTransform: 'uppercase',
                                letterSpacing: '0.06em', fontWeight: 500, marginBottom: 4 }}>
                    <span>Inverterid (valikuline)</span>
                    {canAddDevices && <button onClick={() => addInverter(p.slot)} style={{
                      padding: '3px 8px', borderRadius: 3,
                      border: '1px solid var(--border-strong)', background: 'white',
                      color: 'var(--text-2)', fontSize: '0.62rem', fontWeight: 500, cursor: 'pointer',
                      textTransform: 'none', letterSpacing: 0,
                    }}>+ Lisa inverter</button>}
                  </div>
                  {(p.inverters || []).length === 0 && (
                    <div style={{ fontSize: '0.66rem', color: 'var(--text-4)', fontStyle: 'italic', padding: '4px 0' }}>
                      Pole konfigureeritud invertereid
                    </div>
                  )}
                  {(p.inverters || []).map((inv, idx) => (
                    <div key={idx} style={{
                      display: 'grid', gridTemplateColumns: '1.4fr 1fr 90px 90px 1fr 32px',
                      gap: 6, marginBottom: 4, alignItems: 'center',
                    }}>
                      <select
                        value={inv.type}
                        onChange={(e) => updateInverter(p.slot, idx, 'type', e.target.value)}
                        style={{ padding: '5px 7px', borderRadius: 3, border: '1px solid var(--border-strong)',
                                 fontSize: '0.7rem', fontFamily: 'JetBrains Mono, monospace', background: 'white' }}
                      >
                        {inverterTypes.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                      </select>
                      <CellInput value={inv.host || ''} mono
                        onChange={(v) => updateInverter(p.slot, idx, 'host', v)} placeholder="10.0.0.X" />
                      <CellInput value={inv.port ?? ''} type="number" mono
                        onChange={(v) => updateInverter(p.slot, idx, 'port', parseInt(v) || null)} placeholder="502" />
                      <CellInput value={inv.slave_id ?? ''} type="number" mono
                        onChange={(v) => updateInverter(p.slot, idx, 'slave_id', parseInt(v) || null)} placeholder="1" />
                      <CellInput value={inv.label || ''}
                        onChange={(v) => updateInverter(p.slot, idx, 'label', v)} placeholder="Silt" />
                      <button
                        onClick={() => removeInverter(p.slot, idx)}
                        title="Eemalda inverter"
                        style={{
                          padding: '5px', borderRadius: 3,
                          border: '1px solid var(--border-strong)', background: 'white',
                          color: 'var(--negative)', cursor: 'pointer',
                          fontSize: '0.85rem', lineHeight: 1,
                        }}
                      >×</button>
                    </div>
                  ))}
                </div>
                </>)}
              </div>
            ))}
            {canAddDevices && (
              <button onClick={addPark} style={{ ...addBtnStyle, marginTop: 4 }}>+ Lisa park</button>
            )}
          </Section>
        )}
    </div>
  );
}

// =====================================================================
// Deye seaded — otse inverterist (GET /api/parks/:slot/deye-settings), AINULT lugemiseks
// =====================================================================
function DeyeSettingsView({ slot }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showDocumented, setShowDocumented] = useState(false);
  const [exporting, setExporting] = useState(false);
  // AI-seadistuste analüüsi eksport: kõik seaded + dokumendi vahemikud JSON-failina
  const downloadExport = async () => {
    setExporting(true);
    try {
      const r = await fetch(`/api/parks/${slot}/deye-settings/export`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      const url = URL.createObjectURL(new Blob([JSON.stringify(j, null, 2)], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url; a.download = `deye-seaded-slot${slot}-${new Date().toISOString().slice(0, 10)}.json`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); } finally { setExporting(false); }
  };
  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`/api/parks/${slot}/deye-settings`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setData(j);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { setData(null); load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [slot]);

  const kv = (rows) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '4px 18px' }}>
      {rows.map(([k, v, hint]) => (
        <div key={k} title={hint || undefined} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '5px 0', borderBottom: '1px solid var(--divider)', fontSize: '0.74rem' }}>
          <span style={{ color: 'var(--text-3)' }}>{k}</span>
          <span style={{ textAlign: 'right' }}>
            <strong style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>{v ?? '—'}</strong>
            {hint && <span style={{ display: 'block', fontSize: '0.6rem', color: 'var(--text-4)' }}>{hint}</span>}
          </span>
        </div>
      ))}
    </div>
  );
  // Kaardi/dokumendi kirje → [nimi, väärtus, vihje (lubatud vahemik)]
  const itemRow = (it) => [it.name, `${it.value}${it.unit ? ` ${it.unit}` : ''}`,
                           it.range ? `lubatud ${it.range}${it.uncertain ? ' (?)' : ''}` : null];
  const kw = (w) => (w == null ? '—' : `${(w / 1000).toFixed(1)} kW`);

  return (
    <div>
      <div style={{ marginBottom: 12, padding: '10px 14px', background: 'var(--surface)', border: '1px solid var(--border)',
                    borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: '0.72rem', color: 'var(--text-2)' }}>
          Deye seaded loetakse otse inverterist. <strong>Ainult lugemiseks</strong> — EMS ei kirjuta inverterisse;
          muudatused tee Deye / Solarman äpis.
          {error && <span style={{ color: 'var(--negative)', marginLeft: 8 }}><AlertCircle size={12} style={{ display: 'inline', verticalAlign: -2 }} /> {error}</span>}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={downloadExport} disabled={exporting} title="Kõik seaded + lubatud vahemikud JSON-failina (AI-seadistuste analüüsiks)"
            style={{ ...addBtnStyle, display: 'flex', alignItems: 'center', gap: 4 }}>
            {exporting ? 'Loen...' : '⬇ JSON AI-analüüsiks'}
          </button>
          <button onClick={load} disabled={loading} style={{ ...addBtnStyle, display: 'flex', alignItems: 'center', gap: 4 }}>
            <RefreshCw size={12} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /> {loading ? 'Loen...' : 'Värskenda'}
          </button>
        </div>
      </div>
      {!data && loading && <div style={{ padding: 20, color: 'var(--text-3)', fontSize: '0.75rem' }}>Loen inverterist...</div>}
      {(data?.devices || []).map((d) => (
        <div key={d.key} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: 6 }}>
            Deye {d.label || d.key}
            <span style={{ fontSize: '0.66rem', color: 'var(--text-3)', fontWeight: 400, marginLeft: 8, fontFamily: 'JetBrains Mono, monospace' }}>
              {d.key}{d.logger_sn ? ` · logger SN ${d.logger_sn}` : ''}
            </span>
          </div>
          {d.error ? (
            <div style={{ color: 'var(--negative)', fontSize: '0.74rem' }}>Lugemine ebaõnnestus: {d.error}</div>
          ) : (<>
            <Section title="Seade">
              {kv([
                ['Tüüp', d.info.device], ['Inverteri SN', d.info.inverter_sn],
                ['Nimivõimsus', kw(d.info.rated_power_w)], ['MPPT-d / faasid', `${d.info.mppts} / ${d.info.phases}`],
                ['Juhtplaadi püsivara', d.info.fw_control], ['Sideplaadi püsivara', d.info.fw_comm],
              ])}
            </Section>
            <Section title="Töörežiim">
              {kv([
                ['Energiamuster', d.work.energy_pattern], ['Töörežiim', d.work.work_mode],
                ['Ülejäägi eksport', d.work.export_surplus], ['Max eksport', kw(d.work.export_max_w)],
                ['Nullekspordi võimsus', `${d.work.zero_export_w} W`],
                ['Ajakava (Time of Use)', d.work.tou_enabled ? `sees · ${d.work.tou_days.join(' ') || '—'}` : 'väljas'],
              ])}
            </Section>
            {(d.advanced || []).filter((g) => g.group === 'Grid Parameters').map((g) => (
              <Section key={g.group} title="Võrgu parameetrid (kaitsepiirid, võrgukood, taasühendamine)">
                {kv(g.items.map(itemRow))}
              </Section>
            ))}
            <Section title="Ajakava programmid">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.74rem' }}>
                <thead><tr><Th>#</Th><Th>Algus</Th><Th>Lõpp</Th><Th>Võimsus</Th><Th>SoC</Th><Th>Laadimine</Th></tr></thead>
                <tbody>
                  {d.programs.map((pr) => (
                    <tr key={pr.n}><Td>{pr.n}</Td><Td>{pr.start}</Td><Td>{pr.end}</Td><Td>{kw(pr.power_w)}</Td><Td>{pr.soc}%</Td><Td>{pr.charging}</Td></tr>
                  ))}
                </tbody>
              </table>
            </Section>
            <Section title="Aku">
              {kv([
                ['Aku tüüp', d.battery.mode], ['Juhtimine', d.battery.operation],
                ['Mahutavus', `${d.battery.capacity_ah} Ah`], ['Max laadimisvool', `${d.battery.max_charge_a} A`],
                ['Max tühjendusvool', `${d.battery.max_discharge_a} A`], ['Shutdown SoC', `${d.battery.shutdown_soc}%`],
                ['Restart SoC', `${d.battery.restart_soc}%`], ['Madal SoC', `${d.battery.low_soc}%`],
                ['Võrgust laadimine', d.battery.grid_charge], ['Võrgust laadimise algus', `${d.battery.grid_charge_start_soc}%`],
                ['Võrgust laadimise vool', `${d.battery.grid_charge_current_a} A`],
              ])}
            </Section>
            {(d.advanced || []).some((g) => g.group !== 'Grid Parameters') && (
              <div style={{ marginTop: 4 }}>
                <button onClick={() => setShowAdvanced((v) => !v)} style={{ ...addBtnStyle, fontSize: '0.7rem', padding: '5px 10px' }}>
                  {showAdvanced ? '▾' : '▸'} Täpsemad seaded ({d.advanced.filter((g) => g.group !== 'Grid Parameters').reduce((a, g) => a + g.items.length, 0)})
                </button>
                {showAdvanced && d.advanced.filter((g) => g.group !== 'Grid Parameters').map((g) => (
                  <Section key={g.group} title={`Täpsemad — ${g.label}`}>
                    {kv(g.items.map(itemRow))}
                  </Section>
                ))}
              </div>
            )}
            {(d.documented || []).length > 0 && (
              <div style={{ marginTop: 8 }}>
                <button onClick={() => setShowDocumented((v) => !v)} style={{ ...addBtnStyle, fontSize: '0.7rem', padding: '5px 10px' }}>
                  {showDocumented ? '▾' : '▸'} Deye protokolli lisaseaded ({d.documented.reduce((a, g) => a + g.items.length, 0)})
                </button>
                {showDocumented && (<>
                  <div style={{ fontSize: '0.66rem', color: 'var(--text-3)', margin: '6px 0' }}>
                    Deye ametlikust Modbus-protokollist (V104.3 SG01HP3) — kogukonna kaardis puudu. (?) = dokumendis ebaselge rida.
                  </div>
                  {d.documented.map((g) => (
                    <Section key={g.group} title={`Protokoll — ${g.label}`}>
                      {kv(g.items.map(itemRow))}
                    </Section>
                  ))}
                </>)}
              </div>
            )}
          </>)}
        </div>
      ))}
    </div>
  );
}

// Pargi tüüp = parks.json meter_protocol — määrab, milliseid seadmeid pargil seadistatakse
const PARK_TYPES = [
  { id: '', label: 'Standard (ex9EMS + PCS + BAU)' },
  { id: 'sxy88_passthrough', label: 'SXY88 passthrough' },
  { id: 'deye_v5', label: 'Deye hübriid (Solarman V5)' },
];
// PV stringide tabel (pv_strings) — vt backend lib/deyeReport.js pvStringAnalysis
const PV_PANEL_FIELDS = [
  { key: 'pmax_w', label: 'Pmax W', placeholder: '455' },
  { key: 'voc_v', label: 'Voc V', placeholder: '49.6' },
  { key: 'vmp_v', label: 'Vmp V', placeholder: '41.7' },
  { key: 'isc_a', label: 'Isc A', placeholder: '11.6' },
  { key: 'imp_a', label: 'Imp A', placeholder: '10.9' },
  { key: 'temp_coeff_voc_pct', label: 'β Voc %/°C', placeholder: '-0.27', hint: 'Voc temperatuuritegur' },
  { key: 'temp_coeff_pmax_pct', label: 'γ Pmax %/°C', placeholder: '-0.35', hint: 'Pmax temperatuuritegur' },
];
const PV_COLUMNS = [
  { key: 'mppt', label: 'MPPT', placeholder: '1' },
  { key: 'label', label: 'Nimi', placeholder: 'Lõunakatus' },
  { key: 'panels_series', label: 'Järjest', placeholder: '14', hint: 'Paneele ühes stringis järjestikku' },
  { key: 'strings_parallel', label: 'Paralleel', placeholder: '1', hint: 'Stringe paralleelselt samal MPPT-l' },
  ...PV_PANEL_FIELDS,
  { key: 'tilt', label: 'Kalle °', placeholder: '35' },
  { key: 'azimuth', label: 'Asimuut °', placeholder: '0', hint: '0 = lõuna, −90 = ida, 90 = lääs' },
];
const PV_NUMERIC_KEYS = ['panels_series', 'strings_parallel', ...PV_PANEL_FIELDS.map((f) => f.key), 'tilt', 'azimuth'];
const PV_GRID = '48px 1.4fr 60px 60px repeat(7, 1fr) 60px 70px 32px';

const selectStyle = {
  padding: '5px 7px', borderRadius: 3, border: '1px solid var(--border-strong)',
  fontSize: '0.7rem', background: 'white',
};
const addBtnStyle = {
  padding: '3px 8px', borderRadius: 3, border: '1px solid var(--border-strong)', background: 'white',
  color: 'var(--text-2)', fontSize: '0.62rem', fontWeight: 500, cursor: 'pointer',
};
const removeBtnStyle = {
  padding: '5px', borderRadius: 3, border: '1px solid var(--border-strong)', background: 'white',
  color: 'var(--negative)', cursor: 'pointer', fontSize: '0.85rem', lineHeight: 1,
};

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{
        fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase',
        letterSpacing: '0.06em', marginBottom: 8, fontWeight: 500,
      }}>
        {title}
      </div>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: 14 }}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder, mono = false }) {
  return (
    <div>
      <div style={{ fontSize: '0.6rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>
        {label}
      </div>
      <input
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%', padding: '5px 8px', borderRadius: 4,
          border: '1px solid var(--border-strong)',
          fontFamily: mono ? 'JetBrains Mono, monospace' : 'inherit',
          fontSize: '0.72rem',
        }}
      />
    </div>
  );
}

function FieldGroup({ label, children }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: '0.6rem', color: 'var(--text-3)', textTransform: 'uppercase',
                    letterSpacing: '0.06em', fontWeight: 500, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 6 }}>
        {children}
      </div>
    </div>
  );
}

// =====================================================================
// Süsteemi seadistused — PCS + BAU write käsklused (testimata)
// =====================================================================
function SystemSettingsPanel() {
  const { data } = useParks();
  const onlineParks = (data?.parks || []).filter(p => p.online && p.bau);
  const [selectedSlot, setSelectedSlot] = useState(onlineParks[0]?.slot ?? null);
  // Parkide andmed võivad laadida hiljem / kliendivaade vahetub → vali esimene nähtav park
  useEffect(() => {
    if (onlineParks.length && !onlineParks.some(p => p.slot === selectedSlot)) setSelectedSlot(onlineParks[0].slot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlineParks.length]);
  const [pcsPower, setPcsPower] = useState(0);
  const [powerLimits, setPowerLimits] = useState({ charge_kw: null, discharge_kw: null, grid_in_a: null, grid_out_a: null });
  const [socMin, setSocMin] = useState(10);
  const [socMax, setSocMax] = useState(90);
  const [socSaving, setSocSaving] = useState(false);
  const [socMsg, setSocMsg] = useState(null);
  const [lcRefrig, setLcRefrig] = useState(28);
  const [lcHeating, setLcHeating] = useState(15);
  const [dehumStart, setDehumStart] = useState(65);
  const [dehumStop, setDehumStop] = useState(55);
  const [heatStart, setHeatStart] = useState(5);
  const [resp, setResp] = useState(null);

  // Kui valitud park muutub, sünkroniseeri väljad **ainult ühe korra** pargi vahetusel.
  // (Kui paneksime `onlineParks` sõltuvusse, resetiks WebSocket-i poll iga sekund kasutaja sisestust.)
  useEffect(() => {
    const p = onlineParks.find(x => x.slot === selectedSlot);
    if (!p?.bau) return;
    setLcRefrig(p.bau.lc_refrigeration_point ?? 28);
    setLcHeating(p.bau.lc_heating_point ?? 15);
    setDehumStart(p.bau.dehum_humidity_start ?? 65);
    setDehumStop(p.bau.dehum_humidity_stop ?? 55);
    setHeatStart(p.bau.heat_start_value ?? 5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSlot]);

  // Lae pargi peakaitse-põhised võimsus-limiidid
  useEffect(() => {
    if (selectedSlot == null) return;
    fetch(`/api/sites/${selectedSlot}/control/limits`)
      .then(r => r.json())
      .then(d => setPowerLimits(d))
      .catch(() => {});
  }, [selectedSlot]);

  // Lae pargi SoC piirid
  useEffect(() => {
    if (selectedSlot == null) return;
    fetch(`/api/sites/${selectedSlot}/control/config`)
      .then(r => r.json())
      .then(d => { setSocMin(d.soc_min ?? 10); setSocMax(d.soc_max ?? 90); })
      .catch(() => {});
  }, [selectedSlot]);

  const saveSocConfig = async () => {
    if (selectedSlot == null) return;
    setSocSaving(true);
    setSocMsg(null);
    try {
      const r = await fetch(`/api/sites/${selectedSlot}/control/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ soc_min: socMin, soc_max: socMax }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'salvestamine ebaõnnestus');
      setSocMsg({ ok: true, text: 'Salvestatud' });
    } catch (e) {
      setSocMsg({ ok: false, text: e.message });
    } finally {
      setSocSaving(false);
      setTimeout(() => setSocMsg(null), 3000);
    }
  };

  // Kui kasutaja on valinud konkreetse pargi, suuname käsud per-site endpointidele
  // (/api/sites/:slot/control/<action>). Vaikepark on globaalne /api/control/<action>.
  const callApi = async (path, body) => {
    setResp({ pending: true });
    let url = path;
    if (selectedSlot && path.startsWith('/api/control/')) {
      const action = path.slice('/api/control/'.length);
      url = `/api/sites/${selectedSlot}/control/${action}`;
    }
    try {
      const r = await fetch(url, {
        method: 'POST',
        // Saada Content-Type ainult siis kui body on olemas — vastasel juhul
        // Fastify keeldub FST_ERR_CTP_EMPTY_JSON_BODY veaga.
        headers: body ? { 'Content-Type': 'application/json' } : {},
        body: body ? JSON.stringify(body) : undefined,
      });
      const j = await r.json().catch(() => ({}));
      setResp({ ok: r.ok, status: r.status, data: j, url });
    } catch (e) {
      setResp({ ok: false, error: e.message, url });
    }
  };

  const selectedPark = onlineParks.find(p => p.slot === selectedSlot);
  const pcsState = selectedPark?.pcs?.pcs_status;
  const pcsStateName = { 0:'Väljas', 1:'Self-Check', 2:'Töötab', 4:'Viga', 7:'Standby', 10:'Laeb', 11:'Tühjeneb' }[pcsState] || `Olek ${pcsState}`;

  // Deye hübriid: PCS/BAU käskude asemel ainult Deye seaded (otse inverterist, lugemiseks)
  const isDeye = selectedPark?.meter_protocol === 'deye_v5';

  return (
    <div>
      {/* Hoiatusriba (PCS/BAU käsud) */}
      {!isDeye && <div style={{
        marginBottom: 16, padding: '10px 14px',
        background: 'rgba(180,83,9,0.08)', border: '1px solid rgba(180,83,9,0.3)',
        borderRadius: 6, display: 'flex', alignItems: 'flex-start', gap: 10,
      }}>
        <AlertTriangle size={16} style={{ color: '#b45309', marginTop: 2, flexShrink: 0 }} />
        <div style={{ fontSize: '0.72rem', color: '#7c2d12', lineHeight: 1.5 }}>
          <strong style={{ color: '#7c2d12' }}>Käsklused on TESTIMATA.</strong>
          {' '}Backend on hetkel READ_ONLY režiimis — kõik write-käsud blokeeritakse turvaliselt.
          Käskude tegelikuks kasutamiseks vaja: <em>(1)</em> kontrollida PCS/BAU write-aadressid manuaalist,
          <em> (2)</em> käivitada backend ilma <code>READ_ONLY=true</code>,
          <em> (3)</em> alguses testida <strong>ainult ühe pargiga</strong>.
        </div>
      </div>}

      {/* Pargi valik */}
      <div style={{
        marginBottom: 16, padding: '10px 14px',
        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: '0.62rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Sihtpark
        </span>
        <select
          value={selectedSlot ?? ''}
          onChange={(e) => setSelectedSlot(parseInt(e.target.value))}
          style={{
            padding: '5px 10px', borderRadius: 4,
            border: '1px solid var(--border-strong)', background: 'white',
            fontSize: '0.78rem', fontFamily: 'inherit',
          }}
        >
          {onlineParks.length === 0 && <option>—</option>}
          {onlineParks.map(p => (
            <option key={p.slot} value={p.slot}>Slot {p.slot} · {p.name}</option>
          ))}
        </select>
        {selectedPark && isDeye && (
          <span style={{ fontSize: '0.66rem', color: 'var(--text-3)' }}>
            Deye hübriid · SOC: <strong>{selectedPark.bau?.soc?.toFixed(0) ?? '—'}%</strong>
            {' · '}Aku T: <strong>{selectedPark.bau?.tcell_avg?.toFixed(1) ?? '—'}°C</strong>
          </span>
        )}
        {selectedPark && !isDeye && (
          <span style={{ fontSize: '0.66rem', color: 'var(--text-3)' }}>
            PCS: <strong>{pcsStateName}</strong>
            {' · '}SOC: <strong>{selectedPark.bau?.soc?.toFixed(1) ?? '—'}%</strong>
            {' · '}Cell T: <strong>{selectedPark.bau?.tcell_min?.toFixed(1) ?? '—'}…{selectedPark.bau?.tcell_max?.toFixed(1) ?? '—'}°C</strong>
          </span>
        )}
      </div>

      {!selectedPark && (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-3)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6 }}>
          Vali online park ülevalt.
        </div>
      )}

      {selectedPark && isDeye && <DeyeSettingsView slot={selectedPark.slot} />}

      {selectedPark && !isDeye && (
        <>
          {/* PCS sektsioon */}
          <Section title="PCS — pinge muundamise süsteem">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
              {/* Set power */}
              <div>
                <Label>Aku võimsuse seadepunkt (kW)</Label>
                <PowerInput
                  value={pcsPower} setValue={setPcsPower}
                  onSend={() => callApi('/api/control/power', { power: pcsPower })}
                  min={powerLimits.discharge_kw != null ? -powerLimits.discharge_kw : -125}
                  max={powerLimits.charge_kw    ?? 125}
                />
                <Hint>
                  Praegu seadmes: {selectedPark.pcs?.p_total?.toFixed(2) ?? '—'} kW
                  {' · '}
                  Vahemik {powerLimits.discharge_kw != null ? `−${powerLimits.discharge_kw}` : '−125'}…{powerLimits.charge_kw ?? 125} kW
                  {powerLimits.grid_in_a != null && (
                    <span style={{ color: '#94a3b8' }}> (peakaitse {powerLimits.grid_in_a} A in / {powerLimits.grid_out_a} A out)</span>
                  )}
                  {' · '}Negatiivne = tühjendamine, positiivne = laadimine.
                </Hint>
              </div>

              {/* Start/Stop/Emergency */}
              <div>
                <Label>PCS olekukäsud</Label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <CmdBtn onClick={() => callApi('/api/control/start')} icon={<Power size={12} />} color="#15803d">
                    Käivita
                  </CmdBtn>
                  <CmdBtn onClick={() => callApi('/api/control/stop')} icon={<Square size={12} />} color="#b45309">
                    Peata
                  </CmdBtn>
                  <CmdBtn onClick={() => {
                    if (confirm('Kindlasti EMERGENCY STOP? See sulgeb PCS-i ja võib BMS lahti ühendada.'))
                      callApi('/api/control/emergency');
                  }} icon={<AlertTriangle size={12} />} color="#b91c1c">
                    Emergency Stop
                  </CmdBtn>
                </div>
              </div>

              {/* Kombineeritud: Connect & Käivita / Peata & Disconnect (BAU + PCS) */}
              <div>
                <Label>Kombineeritud käsklused (BAU + PCS)</Label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <CmdBtn onClick={() => {
                    if (confirm(`Slot ${selectedSlot}: ühenda aku (BAU connect, 0x3001→1) ja seejärel käivita PCS?`))
                      callApi('/api/control/connect-start');
                  }} icon={<Power size={12} />} color="#15803d">
                    Connect & Käivita
                  </CmdBtn>
                  <CmdBtn onClick={() => {
                    if (confirm(`Slot ${selectedSlot}: peata PCS ja seejärel LAHUTA aku võrgust (BAU disconnect, 0x3001→2)?`))
                      callApi('/api/control/stop-disconnect');
                  }} icon={<Square size={12} />} color="#b91c1c">
                    Peata & Disconnect
                  </CmdBtn>
                </div>
                <Hint>
                  Connect = BAU 0x3001→1 (close) → ootab kuni BAU valmis (auto_connect + run_state, max 15 s) → PCS start.
                  Disconnect = PCS stop → 1,5 s → BAU 0x3001→2 (open). Nõuab READ_ONLY väljas + BAU remote-režiimi.
                </Hint>
              </div>

              {/* Mode */}
              <div>
                <Label>Töörežiim</Label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <CmdBtn onClick={() => callApi('/api/control/pcs-running-mode', { mode: 'constant_power' })}>
                    Constant Power
                  </CmdBtn>
                  <CmdBtn onClick={() => callApi('/api/control/pcs-standby', { enabled: true })}>
                    Standby
                  </CmdBtn>
                </div>
              </div>

              {/* SoC piirid — PlanController kasutab battery_use'is */}
              <div>
                <Label>Aku SoC piirid (%)</Label>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-3)' }}>min</span>
                  <input type="number" min={0} max={100} step={1}
                    value={socMin}
                    onChange={(e) => setSocMin(parseInt(e.target.value) || 0)}
                    style={{ ...inputStyle, width: 60, flex: 'none' }} />
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-3)' }}>max</span>
                  <input type="number" min={0} max={100} step={1}
                    value={socMax}
                    onChange={(e) => setSocMax(parseInt(e.target.value) || 0)}
                    style={{ ...inputStyle, width: 60, flex: 'none' }} />
                  <CmdBtn onClick={saveSocConfig} disabled={socSaving}>
                    {socSaving ? 'Salvestan…' : 'Salvesta'}
                  </CmdBtn>
                  {socMsg && (
                    <span style={{ fontSize: '0.72rem', color: socMsg.ok ? '#15803d' : '#b91c1c', marginLeft: 4 }}>
                      {socMsg.text}
                    </span>
                  )}
                </div>
                <Hint>
                  Praegune SoC: {selectedPark.pcs?.bms_soc ?? '—'}% ·
                  Battery_use loop ei tühjenda alla {socMin}% ega lae üle {socMax}%.
                  Viimase 3% piiril vähendab võimsust lineaarselt.
                </Hint>
              </div>
            </div>
          </Section>

          {/* BAU vedelikjahutus */}
          <Section title="BAU — Vedelikjahutus">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
              <div>
                <Label>Refrig. setpoint (°C)</Label>
                <SetpointRow value={lcRefrig} setValue={setLcRefrig} onSend={() =>
                  callApi('/api/control/cooling/setpoint', { type: 'refrigeration', value: lcRefrig })
                } />
                <Hint>Praegu seadmes: {selectedPark.bau?.lc_refrigeration_point?.toFixed(1) ?? '—'} °C</Hint>
              </div>
              <div>
                <Label>Heating setpoint (°C)</Label>
                <SetpointRow value={lcHeating} setValue={setLcHeating} onSend={() =>
                  callApi('/api/control/cooling/setpoint', { type: 'heating', value: lcHeating })
                } />
                <Hint>Praegu seadmes: {selectedPark.bau?.lc_heating_point?.toFixed(1) ?? '—'} °C</Hint>
              </div>
              <div>
                <Label>Süsteem sees/väljas</Label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <CmdBtn onClick={() => callApi('/api/control/cooling/enable', { enabled: true })} icon={<Snowflake size={12} />} color="#15803d">
                    Sisse
                  </CmdBtn>
                  <CmdBtn onClick={() => callApi('/api/control/cooling/enable', { enabled: false })} icon={<Square size={12} />} color="#b91c1c">
                    Välja
                  </CmdBtn>
                </div>
                <Hint>Praegu: {selectedPark.bau?.lc_unit_state ? 'Sees' : 'Väljas'}</Hint>
              </div>
            </div>
          </Section>

          {/* BAU sisekliima */}
          <Section title="BAU — Sisekliima (niiskus, küte)">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
              <div>
                <Label>Niiskuse-eemaldaja käivitus (%)</Label>
                <SetpointRow value={dehumStart} setValue={setDehumStart} onSend={() =>
                  callApi('/api/control/dehum/setpoint', { field: 'start', value: dehumStart })
                } />
                <Hint>Praegu seadmes: {selectedPark.bau?.dehum_humidity_start ?? '—'} %</Hint>
              </div>
              <div>
                <Label>Niiskuse-eemaldaja seiskumine (%)</Label>
                <SetpointRow value={dehumStop} setValue={setDehumStop} onSend={() =>
                  callApi('/api/control/dehum/setpoint', { field: 'stop', value: dehumStop })
                } />
                <Hint>Praegu seadmes: {selectedPark.bau?.dehum_humidity_stop ?? '—'} %</Hint>
              </div>
              <div>
                <Label>Elektrikütte käivitus (°C)</Label>
                <SetpointRow value={heatStart} setValue={setHeatStart} onSend={() =>
                  callApi('/api/control/heat/setpoint', { value: heatStart })
                } />
                <Hint>Praegu seadmes: {selectedPark.bau?.heat_start_value ?? '—'} °C</Hint>
              </div>
              <div>
                <Label>Niiskus — režiim</Label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <CmdBtn onClick={() => callApi('/api/control/dehum/auto', { auto: true })} color="#15803d">
                    Auto
                  </CmdBtn>
                  <CmdBtn onClick={() => callApi('/api/control/dehum/auto', { auto: false })} color="#b45309">
                    Käsi
                  </CmdBtn>
                </div>
                <Hint>
                  Režiim: {selectedPark.bau?.dehum_auto_mode === 1 ? 'Auto' : selectedPark.bau?.dehum_auto_mode === 0 ? 'Käsi' : '—'} ·
                  Olek: {selectedPark.bau?.dehum_state === 1 ? 'Töötab' : selectedPark.bau?.dehum_state === 0 ? 'Seisus' : '—'}
                </Hint>
              </div>
            </div>
          </Section>

          {/* BAU reset / muu */}
          <Section title="BAU — Muu">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <CmdBtn onClick={() => callApi('/api/control/reset')} color="#b45309">
                BAU Reset (alarmid kustuta)
              </CmdBtn>
            </div>
          </Section>

          {/* Vastus */}
          {resp && (
            <div style={{
              marginTop: 12, padding: '10px 14px',
              background: resp.pending ? 'var(--surface-2)'
                : resp.ok ? 'rgba(21,128,61,0.06)'
                : 'rgba(220,38,38,0.06)',
              border: '1px solid ' + (resp.ok ? 'rgba(21,128,61,0.22)' : 'rgba(220,38,38,0.22)'),
              borderRadius: 6, fontSize: '0.72rem',
              fontFamily: 'JetBrains Mono, monospace',
              color: resp.ok ? 'var(--positive)' : 'var(--negative)',
            }}>
              {resp.pending ? 'Saadan...' : (
                <>
                  HTTP {resp.status} · {JSON.stringify(resp.data || resp.error)}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PowerInput({ value, setValue, onSend, min = -125, max = 125 }) {
  const [raw, setRaw] = useState(String(value));
  useEffect(() => { setRaw(String(value)); }, [value]);
  const commit = (s) => {
    setRaw(s);
    const n = parseFloat(s);
    if (!Number.isNaN(n)) {
      // Clamp peakaitse-põhise limiidi juurde — frontend täiendav kaitse, backend valideerib samuti.
      const clamped = Math.max(min, Math.min(max, n));
      setValue(clamped);
    }
  };
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input
        type="number" min={min} max={max} step={1}
        value={raw}
        onChange={(e) => commit(e.target.value)}
        onBlur={() => { if (raw === '' || Number.isNaN(parseFloat(raw))) setRaw(String(value)); }}
        style={inputStyle}
      />
      <CmdBtn onClick={onSend} icon={<Zap size={12} />}>Saada</CmdBtn>
    </div>
  );
}

function SetpointRow({ value, setValue, onSend }) {
  // Hoiame sisendi toore string-väärtuse, et "0" oleks kustutatav ja kasutaja saaks vabalt sisestada.
  const [raw, setRaw] = useState(String(value));
  useEffect(() => { setRaw(String(value)); }, [value]);
  const commit = (s) => {
    setRaw(s);
    const n = parseFloat(s);
    if (!Number.isNaN(n)) setValue(n);
  };
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input
        type="number"
        value={raw}
        onChange={(e) => commit(e.target.value)}
        onBlur={() => { if (raw === '' || Number.isNaN(parseFloat(raw))) setRaw(String(value)); }}
        style={inputStyle}
      />
      <CmdBtn onClick={onSend}>Saada</CmdBtn>
    </div>
  );
}

const inputStyle = {
  flex: 1, padding: '5px 8px', borderRadius: 4,
  border: '1px solid var(--border-strong)',
  fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem',
};

function Label({ children }) {
  return (
    <div style={{ fontSize: '0.6rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4, fontWeight: 500 }}>
      {children}
    </div>
  );
}

function Hint({ children }) {
  return (
    <div style={{ marginTop: 4, fontSize: '0.62rem', color: 'var(--text-4)', fontStyle: 'italic' }}>
      {children}
    </div>
  );
}

function CmdBtn({ children, onClick, icon, color = '#18181b' }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '5px 10px', borderRadius: 4,
        border: `1px solid ${color}`, background: color, color: 'white',
        cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.7rem', fontWeight: 500,
      }}
    >
      {icon} {children}
    </button>
  );
}

function Th({ children }) {
  return <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '0.6rem', textTransform: 'uppercase', color: 'var(--text-3)', fontWeight: 500, letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{children}</th>;
}
function Td({ children }) {
  return <td style={{ padding: '4px 8px' }}>{children}</td>;
}
function CellInput({ value, onChange, placeholder, type = 'text', mono = false, style: extraStyle }) {
  return (
    <input
      type={type}
      value={value ?? ''}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: '100%', minWidth: 90, padding: '4px 6px', borderRadius: 3,
        border: '1px solid var(--border)', background: 'transparent',
        fontFamily: mono ? 'JetBrains Mono, monospace' : 'inherit',
        fontSize: '0.7rem',
        ...(extraStyle || {}),
      }}
    />
  );
}

export default ServiceSettingsPage;
